import { useState, useCallback } from 'react';
import { Upload, FileText, Loader2 } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ResumeEnhancementReview } from './ResumeEnhancementReview';

interface ResumeUploaderProps {
  onUploadComplete?: () => void;
}

export function ResumeUploader({ onUploadComplete }: ResumeUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showEnhancementReview, setShowEnhancementReview] = useState(false);
  const [uploadedResumeId, setUploadedResumeId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<any>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const { toast } = useToast();

  const handleFileUpload = async (file: File) => {
    console.log('Starting file upload:', file.name);
    
    try {
      setProgress(0);
      setUploading(true);
      setUploadedFile(file);

      // Validate file type
      const validTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'text/plain'
      ];
      
      if (!validTypes.includes(file.type)) {
        toast({
          variant: 'destructive',
          title: 'Invalid file type',
          description: 'Please upload PDF, DOCX, DOC, or TXT',
        });
        setUploading(false);
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          variant: 'destructive',
          title: 'File too large',
          description: 'Maximum size is 5MB',
        });
        setUploading(false);
        return;
      }

      setProgress(10);
      console.log('File validation passed');

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('Auth error:', userError);
        toast({
          variant: 'destructive',
          title: 'Authentication required',
          description: 'Please log in to upload resumes',
        });
        setUploading(false);
        return;
      }

      console.log('User authenticated:', user.id);
      setProgress(20);

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const timestamp = Date.now();
      const fileName = `${user.id}/${timestamp}.${fileExt}`;
      
      console.log('Uploading to storage:', fileName);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        toast({
          variant: 'destructive',
          title: 'Upload failed',
          description: uploadError.message,
        });
        setUploading(false);
        return;
      }

      console.log('File uploaded to storage:', uploadData);
      setProgress(50);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('resumes')
        .getPublicUrl(fileName);

      console.log('Public URL:', publicUrl);
      setProgress(60);

      // Call parse-resume edge function
      console.log('Calling parse-resume edge function...');
      
      const { data: parseData, error: parseError } = await supabase.functions.invoke('parse-resume', {
        body: {
          userId: user.id,
          fileUrl: publicUrl,
          fileName: fileName,
          fileType: file.type,
          fileSize: file.size
        }
      });

      // Log full response for debugging
      console.log('Parse function response:', { data: parseData, error: parseError });

      let finalParseData = parseData;

      if (parseError) {
        console.error('Parse error details:', parseError);
        
        toast({
          title: 'Using basic info',
          description: 'Could not fully parse resume, using filename for now.',
        });
        
        // Use fallback parsed data
        finalParseData = {
          parsedResume: {
            fullName: file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ').replace(/resume/gi, '').trim(),
            email: user.email || '',
            phone: '',
            location: '',
            linkedinUrl: '',
            portfolioUrl: '',
            headline: '',
            targetRoles: [],
            experiences: [],
            education: [],
            skills: []
          },
          confidence: 0
        };
      }

      console.log('Resume data to save:', finalParseData);
      setProgress(80);

      // Insert into uploaded_resumes table
      console.log('Inserting into database...');
      
      const { data: resumeData, error: insertError } = await supabase
        .from('uploaded_resumes')
        .insert({
          user_id: user.id,
          file_name: file.name,
          file_url: publicUrl,
          file_type: file.type,
          file_size: file.size,
          parsed_data: finalParseData?.parsedResume || finalParseData,
          parse_confidence: finalParseData?.confidence || 0
        })
        .select()
        .single();

      if (insertError) {
        console.error('Database insert error:', insertError);
        toast({
          variant: 'destructive',
          title: 'Failed to save resume',
          description: insertError.message,
        });
        setUploading(false);
        return;
      }

      console.log('Resume saved to database:', resumeData);
      setProgress(100);
      
      toast({
        title: 'Resume uploaded successfully!',
        description: 'Getting AI enhancement suggestions...',
      });
      
      // Get AI enhancement suggestions
      setUploadedResumeId(resumeData.id);
      await getEnhancementSuggestions(resumeData.id);
      
      // Refresh resume list
      if (onUploadComplete) {
        await onUploadComplete();
      }

    } catch (error: any) {
      console.error('Unexpected error during upload:', error);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: error.message || 'An unexpected error occurred',
      });
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      handleFileUpload(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'text/plain': ['.txt']
    },
    maxFiles: 1,
    disabled: uploading
  });

  const getEnhancementSuggestions = async (resumeId: string) => {
    setLoadingSuggestions(true);
    try {
      const { data, error } = await supabase.functions.invoke('enhance-uploaded-resume', {
        body: { uploadedResumeId: resumeId }
      });

      if (error) throw error;

      setSuggestions(data);
      setShowEnhancementReview(true);
    } catch (error) {
      console.error('Enhancement error:', error);
      toast({
        title: 'Could not get suggestions',
        description: 'Resume was uploaded but enhancement suggestions failed.',
        variant: 'destructive'
      });
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleEnhancementComplete = () => {
    setShowEnhancementReview(false);
    setSuggestions(null);
    setUploadedResumeId(null);
  };

  const handleEnhancementCancel = () => {
    setShowEnhancementReview(false);
    setSuggestions(null);
    setUploadedResumeId(null);
    toast({
      title: 'Enhancement cancelled',
      description: 'Your original resume was saved without enhancements.',
    });
  };

  return (
    <>
      <Card
        {...getRootProps()}
        className={`p-8 border-2 border-dashed cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-primary hover:bg-primary/5'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        
        <div className="flex flex-col items-center justify-center text-center space-y-4">
          {uploading ? (
            <>
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
              <div className="w-full max-w-xs space-y-2">
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-muted-foreground">
                  Uploading {uploadedFile?.name}... {progress}%
                </p>
              </div>
            </>
          ) : (
            <>
              {isDragActive ? (
                <Upload className="w-12 h-12 text-primary" />
              ) : (
                <FileText className="w-12 h-12 text-muted-foreground" />
              )}
              <div>
                <p className="text-lg font-medium">
                  {isDragActive ? 'Drop your resume here' : 'Drag & drop your resume'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  or click to browse
                </p>
              </div>
              <div className="flex gap-2 text-xs text-muted-foreground">
                <span className="px-2 py-1 bg-muted rounded">PDF</span>
                <span className="px-2 py-1 bg-muted rounded">DOCX</span>
                <span className="px-2 py-1 bg-muted rounded">DOC</span>
                <span className="px-2 py-1 bg-muted rounded">TXT</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Maximum file size: 5MB
              </p>
            </>
          )}
        </div>
      </Card>

      {/* Enhancement Review Modal */}
      <Dialog open={showEnhancementReview} onOpenChange={setShowEnhancementReview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>AI Resume Enhancement</DialogTitle>
            <DialogDescription>
              Review and approve AI-suggested improvements to your resume
            </DialogDescription>
          </DialogHeader>
          
          {loadingSuggestions ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground">Analyzing your resume...</p>
            </div>
          ) : suggestions && uploadedResumeId ? (
            <ResumeEnhancementReview
              uploadedResumeId={uploadedResumeId}
              suggestions={suggestions}
              onComplete={handleEnhancementComplete}
              onCancel={handleEnhancementCancel}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
